// Type Definitions for MathXY

// Available (implemented) topics
export type TopicType =
  | 'arithmetic' // Legacy - backward compatibility
  | 'addition' // Addition only
  | 'subtraction' // Subtraction only
  | 'multiplication' // Multiplication only
  | 'division' // Division only
  | 'factors' // Finding factors
  | 'primes' // Prime number identification
  | 'factorials' // Factorial calculations
  | 'sequences'
  | 'time'
  | 'money'
  | 'length' // Length/distance measurement
  | 'weight' // Mass/weight measurement
  | 'capacity' // Volume/capacity measurement
  | 'unit-conversion' // General unit conversions
  | 'temperature'; // Temperature scales

export const TOPIC_TYPES = [
  'arithmetic',
  'addition',
  'subtraction',
  'multiplication',
  'division',
  'factors',
  'primes',
  'factorials',
  'sequences',
  'time',
  'money',
  'length',
  'weight',
  'capacity',
  'unit-conversion',
  'temperature',
] as const satisfies readonly TopicType[];

export const DEFAULT_TOPIC_TYPE: TopicType = 'addition';

export function isTopicType(value: unknown): value is TopicType {
  return typeof value === 'string' && (TOPIC_TYPES as readonly string[]).includes(value);
}

// All topic identifiers including coming soon
export type TopicId =
  | TopicType
  // Pre-Algebra (coming soon)
  | 'negative-numbers'
  | 'exponents'
  | 'square-roots'
  | 'order-of-operations'
  | 'variables'
  | 'expressions'
  | 'inequalities'
  | 'ratios'
  // Fractions (coming soon)
  | 'fraction-basics'
  | 'equivalent-fractions'
  | 'fraction-addition'
  | 'decimals'
  | 'fraction-decimal'
  | 'percentages'
  // Geometry (coming soon)
  | 'shape-recognition'
  | 'perimeter'
  | 'area'
  | 'volume'
  | 'angles'
  | 'coordinate-plane'
  // Measurement (coming soon)
  | 'length'
  | 'weight'
  | 'capacity'
  | 'unit-conversion'
  | 'temperature'
  // Statistics (coming soon)
  | 'mean-median-mode'
  | 'probability'
  | 'combinations'
  | 'permutations'
  | 'data-charts'
  // Algebra (coming soon)
  | 'linear-equations'
  | 'quadratics'
  | 'graphing'
  | 'systems'
  | 'polynomials';

// Topic categories for UI grouping
export type TopicCategory =
  | 'arithmetic' // Basic operations
  | 'number-theory' // Factors, primes, factorials
  | 'patterns' // Sequences
  | 'time-money' // Time and money
  | 'fractions' // Coming soon
  | 'geometry' // Coming soon
  | 'measurement' // Coming soon
  | 'pre-algebra' // Coming soon
  | 'algebra' // Coming soon
  | 'statistics'; // Coming soon

// Topic status for coming soon badges
export type TopicStatus = 'available' | 'coming-soon';

export type AgeGroup =
  | '4-5'
  | '5-6'
  | '6-7'
  | '7-8'
  | '8-9'
  | '9-10'
  | '10-11'
  | '11-12'
  | '12-13'
  | '13-14'
  | '14-15'
  | '15-16'
  | '16-17'
  | '17-18';

export type Difficulty = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export type Operation = 'add' | 'subtract' | 'multiply' | 'divide';

/**
 * Pattern families a Number Sequences session can draw from.
 * Covers the K-12 span: arithmetic/geometric progressions through the
 * classic "figurate" and recursive sequences taught in upper grades.
 */
export type SequencePatternType =
  | 'arithmetic' // constant common difference (counting + skip counting)
  | 'geometric' // constant common ratio (doubling, ×3, ×10, ...)
  | 'square' // 1, 4, 9, 16, ... (perfect squares)
  | 'triangular' // 1, 3, 6, 10, ... (triangular numbers)
  | 'fibonacci'; // each term is the sum of the previous two

export const SEQUENCE_PATTERN_TYPES = [
  'arithmetic',
  'geometric',
  'square',
  'triangular',
  'fibonacci',
] as const satisfies readonly SequencePatternType[];

/**
 * Which term in the displayed sequence is blanked out.
 * - 'next': the term after the last shown ("what comes next?")
 * - 'interior': a middle term (fill the gap)
 * - 'first': the leading term (count backward to find it)
 */
export type SequenceMissingPosition = 'next' | 'interior' | 'first';

export const SEQUENCE_MISSING_POSITIONS = [
  'next',
  'interior',
  'first',
] as const satisfies readonly SequenceMissingPosition[];

export type UserRole = 'guest' | 'parent' | 'student' | 'admin';

export interface User {
  id: string;
  email?: string;
  role: UserRole;
  isGuest: boolean;
  displayName: string;
  avatar: string;
  createdAt: string;
}

export interface ParentUser extends User {
  role: 'parent' | 'admin';
  subscriptionTier: 'free' | 'premium';
  parentalPin?: string;
  students: StudentUser[];
}

export interface StudentUser extends User {
  role: 'student';
  parentId: string;
  ageGroup: AgeGroup;

  // Parental controls
  maxDailyTime?: number; // minutes
  allowedTopics?: TopicType[];
  difficultyLocked?: boolean;

  // Progress
  currentLevel: number;
  totalPoints: number;
  sessionsCompleted: number;
  correctAnswers: number;
  totalQuestions: number;
  accuracy: number;
  currentStreak: number;
  bestStreak: number;
  preferences: UserPreferences;
}

export interface GuestUser extends User {
  role: 'guest';
  questionsRemaining: number;
  sessionId: string;
  ageGroup: AgeGroup;

  // Progress (stored in localStorage)
  currentLevel: number;
  totalPoints: number;
  sessionsCompleted: number;
  correctAnswers: number;
  totalQuestions: number;
  accuracy: number;
  currentStreak: number;
  bestStreak: number;
  preferences: UserPreferences;
}

// Type guard for checking if a user has practice statistics
export type PracticeProfile = StudentUser | GuestUser;

export function isPracticeProfile(user: User | null): user is PracticeProfile {
  return user !== null && (user.role === 'student' || user.role === 'guest');
}

export interface UserProfile extends User {
  ageGroup?: AgeGroup;
  currentLevel: number;
  totalPoints: number;
  sessionsCompleted: number;
  correctAnswers: number;
  totalQuestions: number;
  accuracy: number;
  currentStreak: number;
  bestStreak: number;
  preferences: UserPreferences;
}

export interface UserPreferences {
  soundEnabled: boolean;
  musicEnabled: boolean;
  feedbackEnabled: boolean;
  maxQuestionsPerSession?: number;
  difficulty: 'auto' | 'manual';
  theme: 'dark' | 'light';
}

/**
 * The kind of answer a question expects, declared by the engine that builds it.
 * The practice screen uses it to pick the input, and grading uses it to compare.
 * The negative and decimal flags must come from the question type and settings,
 * never from the specific answer: a minus key shown only when the answer is
 * negative would give the sign away.
 */
export type AnswerFormat =
  | { kind: 'number'; allowNegative?: boolean; allowDecimal?: boolean }
  | { kind: 'time' } // "3:30"
  | { kind: 'mixed-units'; units: [string, string] } // "2m 35cm" -> ['m', 'cm']
  | { kind: 'choice' }; // picked from the options: Yes/No, words, "which of these"

export interface Question {
  id: string;
  topicType: TopicType;
  difficulty: Difficulty;
  question: string;
  correctAnswer: string | number;
  options?: (string | number)[];
  imageUrl?: string;
  concept: string; // e.g., "addition-under-10", "time-hours"
  // Optional explicit operands for arithmetic-style questions to avoid parsing text
  operands?: number[];
  /**
   * Optional per-question input override. When set, the practice UI uses this
   * input method for this question instead of the session-level setting — e.g.
   * an "nth term" sequence question that needs free numeric entry rather than
   * multiple choice. Leave undefined to inherit the session setting.
   */
  preferredInputType?: 'multiple-choice' | 'text-input' | 'number-pad';
  /**
   * What kind of answer the question expects. Optional so a question built
   * without one still works: grading then infers the format from correctAnswer.
   */
  answerFormat?: AnswerFormat;
}

export interface PracticeSession {
  id: string;
  userId: string;
  topicType: TopicType;
  startTime: string;
  endTime?: string;
  questions: QuestionResult[];
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number; // percent 0-100, from calculateAccuracy (not 0-1)
  pointsEarned: number;
  difficulty: Difficulty;
  completed: boolean;
  supabaseSessionId?: string; // Track if already saved to Supabase (for update vs insert)
  maxQuestions?: number; // the session's question cap, set at start; a saved session that reached it is finished
}

export interface QuestionResult {
  questionId: string;
  question: Question;
  userAnswer: string | number;
  isCorrect: boolean;
  timeSpent: number; // milliseconds
  attempts: number;
  timestamp: string;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  avatar: string;
  score: number;
  sessionsCompleted: number;
  accuracy: number;
  period: 'daily' | 'weekly' | 'monthly' | 'allTime';
}

export interface ConceptMastery {
  concept: string;
  topicType: TopicType;
  totalAttempts: number;
  correctAttempts: number;
  accuracy: number;
  currentDifficulty: Difficulty;
  masteryLevel: 'beginner' | 'learning' | 'practicing' | 'proficient' | 'mastered';
  lastPracticed: string;
}

export interface TopicConfig {
  topicType: TopicId; // Can be available or coming-soon
  name: string;
  shortName?: string; // Optional short name for compact display (e.g., "Add" for "Addition")
  description: string;
  icon: string; // IconName from Icon.tsx (e.g., 'addition', 'subtraction')
  color: string;
  ageGroups: AgeGroup[];
  concepts: string[];
  maxDifficulty: Difficulty;
  // New fields for categorization
  category: TopicCategory;
  gradeRange: string; // e.g., "K-2", "3-5", "6-8"
  status: TopicStatus;
}

// Category configuration for UI display
export interface CategoryConfig {
  id: TopicCategory;
  name: string;
  icon: string;
  color: string;
  gradeRange: string;
  description: string;
  order: number; // Display order
}

export interface AppConfig {
  maxGuestQuestions: number;
  maxQuestionsPerSession: number; // Hard limit for all sessions
  defaultQuestionsPerSession: number;
  pointsPerCorrectAnswer: number;
  pointsPerStreak: number;
  masteryThreshold: number; // e.g., 80% accuracy
  adaptiveLearning: {
    enabled: boolean;
    adjustmentThreshold: number;
    minConsecutiveForIncrease: number;
    minConsecutiveForDecrease: number;
  };
}

export interface Phrase {
  type: 'praise' | 'encouragement';
  text: string;
  context: 'correct' | 'incorrect' | 'struggling' | 'mastery';
}

export interface PerformanceMetrics {
  accuracy: number; // 0-1
  streak: number;
  averageTime: number; // milliseconds
  totalAttempts: number;
  correctAnswers: number;
}
